// Cave84 — Notification Center (Phase 7 Week 19)
// Spec §4.15 F15.2. Bell icon + dropdown.
// Inserted into TopBar between search and user menu.

import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Notification } from '@cave84/shared-types';
import {
  useNotifications,
  useUnreadCount,
  useMarkRead,
  useMarkAllRead,
} from '@/hooks/useNotifications';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(ts: unknown): string {
  try {
    const d =
      typeof (ts as { toDate?: () => Date }).toDate === 'function'
        ? (ts as { toDate: () => Date }).toDate()
        : new Date((ts as { seconds: number }).seconds * 1000);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch {
    return '';
  }
}

const TYPE_ICON: Record<string, string> = {
  drinking_window_enter: '🍷',
  drinking_window_exit:  '⏰',
  batch_complete:        '✅',
  backup_failure:        '⚠️',
  price_change_significant: '📈',
  wishlist_match:        '★',
  drinking_window_change: '🔄',
  analysis_pending:      '📝',
};

// ---------------------------------------------------------------------------
// Single notification row
// ---------------------------------------------------------------------------

function NotifRow({ notif, onRead }: {
  notif: Notification;
  onRead: (id: string) => void;
}) {
  const navigate = useNavigate();

  function handleClick() {
    if (!notif.read) onRead(notif.id);
    if (notif.link) navigate(notif.link);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-surface-3 transition-colors border-b border-border-subtle last:border-0 ${
        notif.read ? 'opacity-60' : ''
      }`}
    >
      <span className="flex-shrink-0 text-base mt-0.5">
        {TYPE_ICON[notif.type] ?? '·'}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm leading-snug ${notif.read ? 'text-text-secondary' : 'text-text-primary font-medium'}`}>
            {notif.title}
          </p>
          {!notif.read && (
            <span className="w-2 h-2 rounded-full bg-gold flex-shrink-0 mt-1.5" />
          )}
        </div>
        <p className="text-xs text-text-muted mt-0.5 leading-relaxed">{notif.body}</p>
        <p className="text-[11px] text-text-muted mt-1">{timeAgo(notif.created_at)}</p>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Bell button + panel
// ---------------------------------------------------------------------------

export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const unreadCount = useUnreadCount();
  const { data: notifications = [] } = useNotifications();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={panelRef} className="relative">
      {/* Bell button */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        className="relative p-2 rounded-md text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-gold text-[11px] font-bold text-black flex items-center justify-center leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 rounded-xl border border-border-medium bg-surface-1 shadow-2xl overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
            <h2 className="text-sm font-semibold text-text-primary">
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-gold/20 text-gold text-xs">
                  {unreadCount}
                </span>
              )}
            </h2>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="text-xs text-text-muted hover:text-gold transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-10 text-center">
                <p className="text-2xl mb-2">🔔</p>
                <p className="text-sm text-text-muted">No notifications yet</p>
              </div>
            ) : (
              notifications.map(n => (
                <NotifRow
                  key={n.id}
                  notif={n}
                  onRead={id => markRead.mutate(id)}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
